import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import type { UserRole } from '../models/session.model';
import { RoleService } from '../services/role.service';
import { AuthService } from '../services/auth.service';

/**
 * Guard declarativo de rol.
 *
 * Uso en rutas compartidas (admin + marketer):
 *   canActivate: [generalStateGuard, roleGuard],
 *   data: { roles: ['admin', 'marketer'] }
 *
 * Se compone DESPUÉS de generalStateGuard para garantizar que el
 * estado de la app esté estabilizado y la sesión ya esté en caché.
 * Por tanto, RoleService ya está hidratado cuando este guard corre.
 */
export const roleGuard: CanActivateFn = (route) => {
  const roleService = inject(RoleService);
  const router = inject(Router);
  const allowedRoles = route.data?.['roles'] as UserRole[] | undefined;

  // Sin restricción de rol explícita → permitir (lo gestiona otro mecanismo)
  if (!allowedRoles || allowedRoles.length === 0) return true;

  const role = roleService.ensureRole();

  // Rol aún no hidratado: NO redirigir. generalStateGuard es la autoridad
  // de sesión/estado y ya decidió antes que este guard; redirigir a
  // /dashboard aquí crearía un bucle infinito (/dashboard también tiene
  // roleGuard) y cancelaría la navegación.
  if (!role) return true;

  if (allowedRoles.includes(role)) return true;

  // Redirigir al dashboard (hogar común; el contenido se acota por rol)
  return router.parseUrl('/dashboard');
};

/**
 * En recarga en frío sobre una URL con canMatch, este corre ANTES que
 * generalStateGuard (canMatch precede a canActivate), con el rol aún
 * null. Hidrata la sesión — cacheada tras la primera llamada, así que
 * las siguientes evaluaciones son síncronas en la práctica.
 */
async function ensureRoleHydrated(roleService: RoleService): Promise<void> {
  if (roleService.ensureRole() === null) {
    await inject(AuthService).getSessionInfo();
  }
}

/**
 * CanMatch para rutas exclusivas de admin.
 * Hace que la ruta sea literalmente inexistente para un marketer:
 * cae al wildcard `**` sin que el router la considere.
 */
export const adminOnlyCanMatch: CanMatchFn = async () => {
  const roleService = inject(RoleService);
  await ensureRoleHydrated(roleService);
  return roleService.isAdmin();
};

/**
 * CanMatch para rutas exclusivas de marketer.
 */
export const marketerOnlyCanMatch: CanMatchFn = async () => {
  const roleService = inject(RoleService);
  await ensureRoleHydrated(roleService);
  return roleService.isMarketer();
};
